import { z } from "zod";

// InboxItem — a per-recipient pointer into "something that stays true
// until you read it or act on it" (next/inbox-and-notifications.md,
// SPEC — fetta 1). Sibling in FORM to CoachAthleteEdge
// (./coachAthleteEdge.ts) but deliberately NOT in shape: an edge is
// TWO rows, one per partition, because both sides of the relationship
// need to read it from their own partition. An InboxItem has exactly
// ONE recipient, so it is a SINGLE row, in the RECEIVER's partition —
// pk=USER#<sub>, sk=INBOX#<createdAt>#<id> (built in
// apps/api/src/lib/ddbKeys.ts). Never mirrored into a second
// partition: that is the exact hole LEGAL-6 had to close for edges,
// and this row's whole point is to fall into the account-deletion
// cascade (routes/me.ts's `pk = :pk`-only drain) on its own.
//
// The row is a POINTER, not a second source of truth. A `proposal`'s
// authoritative state lives on the underlying object (e.g. a
// CoachAthleteEdge) — this row carries `refType` + `refId` back to
// it and nothing more. So, unlike coachEdgeWrites.ts's
// writeEdgeBothSides, there is no second write to keep in sync here:
// one Put (or one Update, on dismiss) is the whole story.
//
// Text is NEVER stored rendered. `titleKey`/`bodyKey` are i18n KEYS,
// optionally interpolated by `params` (string values only — the
// interpolator takes strings, and a second serialisation format
// inside DDB buys nothing). Storing a rendered phrase would freeze
// the inbox in whatever locale was active when the row was written,
// and the other 4 locales would never see it.

export const InboxAudience = z.enum(["athlete", "coach", "admin"]);
export type InboxAudience = z.infer<typeof InboxAudience>;

// `kind` decides the actions, per the spec:
//   - "proposal" : accept / reject. Answering it makes the item
//                  disappear. A derived item (a pending CoachAthleteEdge)
//                  goes when its edge stops being pending. The one STORED
//                  proposal, the MAX proposal of #182, is answered on
//                  /me/max-bumps/:slug, which removes the row; it is never
//                  dismissed.
//   - "notice"   : a plain "✕" — PATCH /me/inbox/:id/dismiss.
export const InboxKind = z.enum(["proposal", "notice"]);
export type InboxKind = z.infer<typeof InboxKind>;

export const InboxItem = z.object({
  id: z.string().min(1),
  createdAt: z.iso.datetime(),
  // ⛔ Filtered by the caller's active Experience, NEVER by account —
  // the same sub can hold athlete/coach/admin inboxes at once
  // (self-coaching, `coachAssignment.ts:141-147`).
  audience: InboxAudience,
  kind: InboxKind,
  // i18n KEYS — see file header. Never a rendered phrase.
  titleKey: z.string().min(1),
  bodyKey: z.string().min(1),
  // Interpolation values for titleKey/bodyKey (e.g. a program name, a
  // status code). String-only by design.
  params: z.record(z.string(), z.string()).optional(),
  refType: z.string().min(1),
  refId: z.string().min(1),
  // Set only on a dismissed `notice`. Absent = still live. A dismissed
  // row is kept (not deleted) — GET /me/inbox filters it out — so the
  // account-deletion cascade still has a row to sweep and there is no
  // separate "already gone" bookkeeping.
  dismissedAt: z.iso.datetime().optional(),
});
export type InboxItem = z.infer<typeof InboxItem>;
